import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js'
import type { CountryCode } from 'libphonenumber-js'
import clsx from 'clsx'

import { CountryFlag } from 'components/atoms/country-flag'
import { IconSvg } from 'components/common'
import { COUNTRY_DATA } from 'utils/constants'

import { formatNationalNumber } from './format-national-number'
import { isSupportedCountry } from './supported-countries'

const asCountryCode = (country: string) => country.toUpperCase() as CountryCode

/** "+1" for "US". Empty for a code libphonenumber does not know, so the field never throws. */
const dialCodeOf = (country: string) => {
	try {
		return `+${getCountryCallingCode(asCountryCode(country))}`
	} catch {
		return ''
	}
}

const COUNTRY_OPTIONS = COUNTRY_DATA.map(({ name, value }) => ({
	name,
	value: value.toUpperCase(),
	dialCode: dialCodeOf(value),
}))

/** How many digits a string holds: the caret keeps its place by this count once the text is re-formatted. */
const countDigits = (text: string) => text.replace(/\D/g, '').length

/** Index just after the `digits`-th digit, or the end when the number has fewer. */
const caretIndexAfterDigits = (text: string, digits: number) => {
	if (digits <= 0) return 0

	let seen = 0
	for (let index = 0; index < text.length; index += 1) {
		if (/\d/.test(text.charAt(index))) {
			seen += 1
			if (seen === digits) return index + 1
		}
	}
	return text.length
}

/**
 * Deleting only a separator leaves the digits unchanged, so the formatter would put the separator
 * straight back and the keystroke would do nothing. Drop the digit before it instead, which is what
 * the member meant, and let the caret land where that digit was.
 */
const applyDeletionIntent = (next: string, previous: string, caret: number) => {
	// Only a backspace over a separator qualifies: exactly one non-digit gone, every digit still
	// there. A replaced value (pasting over the old one) can also be shorter, and must keep its digits.
	if (next.length !== previous.length - 1) return next

	const removed = previous.charAt(caret)
	if (!removed || /\d/.test(removed)) return next
	if (previous.slice(0, caret) + previous.slice(caret + 1) !== next) return next

	const digitIndex = next.slice(0, caret).search(/\d(?=\D*$)/)
	if (digitIndex === -1) return next

	return next.slice(0, digitIndex) + next.slice(digitIndex + 1)
}

/**
 * A number pasted with a leading "+" carries its own country: split it into that country and its
 * national digits so the selector can follow it instead of re-reading it as a national number.
 */
const parseInternationalNumber = (value: string) => {
	const trimmed = value.trim()
	if (!trimmed.startsWith('+')) return undefined

	try {
		const parsed = parsePhoneNumberFromString(trimmed)
		// The selector offers only the allow-list, so a pasted restricted country must not slip in.
		if (!parsed?.country || !isSupportedCountry(parsed.country)) return undefined

		return { country: parsed.country, national: parsed.nationalNumber }
	} catch {
		return undefined
	}
}

export interface PhoneFieldProps {
	/** ISO 3166-1 alpha-2 code, case-insensitive. Selection emits it in lower case. */
	country: string
	onCountryChange: (country: string) => void
	/** The national number only, e.g. "(202) 555-0199". The dial code lives in the selector. */
	value?: string
	onChange?: (value: string) => void
	onBlur?: () => void
	id?: string
	hasError?: boolean
	/** Id of the element that describes the field, e.g. its error message. */
	ariaDescribedBy?: string
	placeholder?: string
	autoComplete?: string
	className?: string
}

/**
 * PhoneField - a 48px phone number control: the country selector (flag, dial code, chevron) joined to a
 * national-number input. The selector's list is anchored through Headless UI, so it renders in a portal
 * instead of being clipped by a scrolling modal body.
 */
export function PhoneField({
	country,
	onCountryChange,
	value = '',
	onChange,
	onBlur,
	id,
	hasError = false,
	ariaDescribedBy,
	placeholder = 'Phone number',
	autoComplete = 'tel-national',
	className,
}: Readonly<PhoneFieldProps>) {
	const dialCode = useMemo(() => dialCodeOf(country), [country])

	const inputRef = useRef<HTMLInputElement>(null)
	// Where the caret should land once the formatted value replaces the typed text.
	const caretRef = useRef<{ value: string; digits: number } | null>(null)

	// The panel renders in a portal, so it cannot inherit the field's width from the DOM: measure the field
	// and give the panel that width, the way a native select's menu matches its control.
	const fieldRef = useRef<HTMLDivElement>(null)
	const [panelWidth, setPanelWidth] = useState<number>()

	useEffect(() => {
		const field = fieldRef.current
		if (!field || typeof ResizeObserver === 'undefined') return

		const observer = new ResizeObserver(() => {
			// clientWidth leaves the 2px borders out, so the panel lines up with the field's inner edges.
			if (field.clientWidth > 0) setPanelWidth(field.clientWidth)
		})
		observer.observe(field)
		return () => observer.disconnect()
	}, [])

	// Re-formatting a controlled input rewrites its value, which puts the caret at the end on every
	// keystroke. Once React has written the formatted text, move it back next to the same digit.
	useLayoutEffect(() => {
		const input = inputRef.current
		const pending = caretRef.current
		if (!input || !pending) return

		caretRef.current = null
		if (input.value !== pending.value) return

		const index = caretIndexAfterDigits(input.value, pending.digits)
		input.setSelectionRange(index, index)
	}, [value])

	return (
		<div
			ref={fieldRef}
			className={clsx(
				'flex h-12 items-center rounded-lg border-2 bg-primary-surface/[0.4] backdrop-blur-lg transition-colors',
				hasError
					? 'border-secondary-red'
					: 'border-translucent-plus-border focus-within:border-accent-surface focus-within:ring-2 focus-within:ring-accent-surface/30',
				className
			)}
		>
			<Listbox value={country.toUpperCase()} onChange={value => onCountryChange(value.toLowerCase())}>
				<ListboxButton
					aria-label={`Country calling code: ${dialCode}`}
					className="flex h-full items-center gap-1.5 rounded-l-lg pl-3 pr-2 text-[15px] font-medium text-primary-text focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-surface/40"
				>
					<CountryFlag code={country} />
					<span>{dialCode}</span>
					<IconSvg icon="dropdown" className="h-4 w-4 text-primary-icon" />
				</ListboxButton>

				{/*
				 * `--anchor-max-height` and `max-h-64` cap the panel before floating-ui measures it, so it
				 * scrolls inside a short panel instead of flipping above the field. The width comes from the
				 * measured field; `w-72` is only the fallback before that measurement lands.
				 * The scrollbar is laid out at the inner border edge, so a transparent right border is what keeps
				 * it off the panel's edge — padding alone cannot move it.
				 */}
				<ListboxOptions
					anchor={{ to: 'bottom start', gap: 8, padding: 16 }}
					style={{ width: panelWidth }}
					className="app-scrollbar z-[100] max-h-64 w-72 overflow-auto rounded-xl border-r-4 border-transparent bg-primary-surface/[0.95] px-2.5 py-1.5 shadow-[0_16px_48px_0px_rgba(0,0,0,0.4)] backdrop-blur-lg [--anchor-max-height:16rem] focus:outline-none"
				>
					{COUNTRY_OPTIONS.map(option => (
						<ListboxOption
							key={option.value}
							value={option.value}
							className="flex cursor-pointer items-center gap-3 rounded-lg py-2 pl-2 pr-3 text-sm text-primary-text data-[focus]:bg-secondary-surface/[0.2] data-[selected]:font-semibold"
						>
							<CountryFlag code={option.value} />
							<span className="min-w-0 flex-1 truncate">{option.name}</span>
							<span className="text-secondary-text">{option.dialCode}</span>
						</ListboxOption>
					))}
				</ListboxOptions>
			</Listbox>

			{/*
			 * @tailwindcss/forms gives every text-like input, `tel` included, a 1px border and a focus ring
			 * of its own. Reset them so the control reads as one field.
			 */}
			<input
				ref={inputRef}
				id={id}
				aria-invalid={hasError || undefined}
				aria-describedby={ariaDescribedBy}
				type="tel"
				inputMode="tel"
				autoComplete={autoComplete}
				placeholder={placeholder}
				value={value}
				onBlur={onBlur}
				onChange={event => {
					const input = event.target
					const international = parseInternationalNumber(input.value)
					if (international) {
						const formatted = formatNationalNumber(international.national, international.country)
						caretRef.current = { value: formatted, digits: countDigits(formatted) }
						onCountryChange(international.country.toLowerCase())
						onChange?.(formatted)
						return
					}

					const caret = input.selectionStart ?? input.value.length
					// Forward Delete next to a separator has the same before/after shape as a backspace, so
					// only the native input type tells them apart.
					const isBackspace = (event.nativeEvent as InputEvent).inputType === 'deleteContentBackward'
					const next = isBackspace ? applyDeletionIntent(input.value, value, caret) : input.value
					const formatted = formatNationalNumber(next, country)
					caretRef.current = { value: formatted, digits: countDigits(next.slice(0, caret)) }
					onChange?.(formatted)
				}}
				className="h-full min-w-0 flex-1 border-0 bg-transparent pl-1 pr-3 text-[15px] font-medium text-primary-text outline-none placeholder:font-normal placeholder:text-secondary-text focus:border-0 focus:outline-none focus:ring-0 focus:shadow-none"
			/>
		</div>
	)
}
